import { Box, Collapse, Grid, Typography, useTheme } from '@mui/material';
import { CardElement, useStripe } from '@stripe/react-stripe-js';
import { PaymentOutlined } from '@mui/icons-material';
import { useFormikContext } from 'formik';
import AddressLookup from '@acorn-portal/shared/src/components/addressLookup';
import FormFieldOptions from '@acorn-portal/shared/src/components/inputs/FormFieldOptions';
import RadioGroupButton from '@acorn-portal/shared/src/components/inputs/radio-button-group';
import FormTextField from '@acorn-portal/shared/src/components/inputs/FormTextField';
import { Address } from '@acorn-portal/shared/src/services/api';
import { FC, useState } from 'react';
import { IRenewalMakePaymentForm } from '@acorn-portal/shared/src/pages/renewal/MakePayment';
import { FieldSet } from '@acorn-portal/shared/src/components/fieldSet';
import { PAYMENT_ERRORS } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import FormField from '@acorn-portal/shared/src/components/inputs/FormField';
import { LoadingButton } from '@mui/lab';
import DirectPaymentVendors from '@acorn-portal/shared/src/components/vendor/DirectPaymentVendors';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import DebitCardSelector from '@acorn-portal/shared/src/components/payment/DebitCardSelector';

export interface StripeCardDetails {
	billingDetails?: 'yes' | 'no';
	cardholderName: string;
	address: Address;
	saveCard: boolean;
}

interface IStripePaymentForm {
	submitting?: boolean;
	useExistingCard?: boolean;
}

const StripePaymentForm: FC<IStripePaymentForm> = ({ submitting, useExistingCard }) => {
	const stripe = useStripe();
	const formik = useFormikContext<IRenewalMakePaymentForm>();
	const renewal = useAppSelector((x) => x.renewal);
	const { renewalDetails } = useAppSelector((x) => x.fullPolicy);
	const [isValid, setIsValid] = useState<boolean>(false);
	const theme = useTheme();

	const handleCardChange = (event: StripeCardElementChangeEvent) => {
		if (event.complete) {
			setIsValid(true);
		} else {
			setIsValid(false);
		}

		if (!event.error) {
			formik.setFieldError('stripe', undefined);
			return;
		}

		if (event.error?.message) {
			formik.setFieldError('stripe', event.error.message);
			return;
		}
		if (!event.brand.includes('visa') && !event.brand.includes('mastercard') && !event.empty) {
			formik.setFieldError('stripe', PAYMENT_ERRORS.vendorType);
			return;
		}

		if (!event.empty && event.complete && !event.error) {
			formik.setFieldError('stripe', undefined);
		}
	};

	const isButtonDisabled = () => {
		if (!useExistingCard) {
			return !stripe || !formik.dirty || !formik.isValid || !isValid || submitting;
		}
		return !formik.values.id || !stripe || !formik.dirty || !formik.isValid || submitting;
	};

	return (
		<FieldSet title=''>
			<Collapse
				in={useExistingCard}
				sx={{
					'* > svg': {
						visibility: 'visible !important',
					},
				}}
			>
				<Box
					sx={{
						margin: 'auto',
						maxWidth: '500px',
						textAlign: 'center',
						pb: '1.5em',
					}}
				>
					{renewal.renewalRatingResponse?.existingCardPaymentMethods && (
						<DebitCardSelector cards={renewal.renewalRatingResponse?.existingCardPaymentMethods} />
					)}
				</Box>
			</Collapse>
			<Collapse
				in={!useExistingCard}
				sx={{
					'* > svg': {
						visibility: 'visible !important',
					},
				}}
			>
				<RadioGroupButton
					label='Is the debit/credit card registered to the same address?'
					name='billingDetails'
					id='billingDetails'
					value={formik.values.billingDetails}
					onblur={() => {
						formik.validateField('address');
						formik.validateField('cardholderFirstName');
						formik.validateField('cardholderLastName');
						formik.validateField('cardholderEmail');
					}}
					onChange={(value) => {
						if (value === 'yes') {
							const policyHolder = renewalDetails?.policyHolder;
							if (!policyHolder) return;
							formik.setValues({
								...formik.values,
								billingDetails: value,
								cardholderFirstName: policyHolder.firstName,
								cardholderLastName: policyHolder.lastName,
								cardholderEmail: policyHolder.email,
								address: policyHolder.address,
							});
						} else {
							formik.setValues({
								...formik.values,
								billingDetails: value as 'yes' | 'no' | undefined,
								cardholderFirstName: '',
								cardholderLastName: '',
								cardholderEmail: '',
								address: {
									line1: '',
									line2: '',
									city: '',
									postcode: '',
								},
							});
						}
					}}
					error={formik.touched.billingDetails && Boolean(formik.errors.billingDetails)}
					helperText={formik.touched.billingDetails && formik.errors.billingDetails}
				/>
				<Collapse in={formik.values.billingDetails === 'no'}>
					<Box>
						<Typography variant='h6' gutterBottom sx={{ textAlign: 'left', fontSize: 18 }}>
							Billing Details
						</Typography>
						<FormTextField
							id='cardholderFirstName'
							name='cardholderFirstName'
							label='Cardholder First Name'
							value={formik.values.cardholderFirstName}
							onChange={formik.handleChange}
							error={formik.touched.cardholderFirstName && Boolean(formik.errors.cardholderFirstName)}
							helperText={formik.touched.cardholderFirstName && formik.errors.cardholderFirstName}
						/>
						<FormTextField
							id='cardholderLastName'
							name='cardholderLastName'
							label='Cardholder Last Name'
							value={formik.values.cardholderLastName}
							onChange={formik.handleChange}
							error={formik.touched.cardholderFirstName && Boolean(formik.errors.cardholderLastName)}
							helperText={formik.touched.cardholderLastName && formik.errors.cardholderLastName}
						/>
						<FormTextField
							autoComplete='email'
							format='email'
							id='cardholderEmail'
							name='cardholderEmail'
							label='Cardholder Email'
							value={formik.values.cardholderEmail}
							onChange={formik.handleChange}
							error={formik.touched.cardholderEmail && Boolean(formik.errors.cardholderEmail)}
							helperText={formik.touched.cardholderEmail && formik.errors.cardholderEmail}
						/>
						<Typography variant='h6' gutterBottom sx={{ textAlign: 'left', fontSize: 18 }}>
							Billing Address
						</Typography>
						<AddressLookup
							// globalError
							initialValue={formik.values.address}
							onChange={(value) => {
								formik.setFieldValue('address', value);
							}}
							firstFieldLabel='Cardholder house number or name'
							postcodeLabel='Cardholder postcode'
						/>
					</Box>
				</Collapse>
				<Box sx={{ mt: 2 }}>
					<FormFieldOptions
						name='saveCard'
						label=''
						value={formik.values.saveCard}
						onChange={(event) => {
							const { value } = event.target;
							formik.setFieldValue('saveCard', value === 'true');
						}}
						options={[
							{
								label: (
									<Typography>
										<b>Save my card details</b> - We will keep your card details stored safely and securely to allow us
										to take any future payments
									</Typography>
								),
								value: true,
							},
							{
								label: (
									<Typography>
										<b>Don’t save my card details</b> - We won&apos;t store your card details and you&apos;ll need to
										give us your payment card details every time you make a change
									</Typography>
								),
								value: false,
							},
						]}
					/>
				</Box>

				<FormField label='' error={Boolean(formik.errors.stripe)} helperText={formik.errors.stripe}>
					<Box
						sx={{
							py: 2,
							px: 1,
							backgroundColor: '#f8f4fc',
							border: `2px solid ${formik.errors.stripe ? theme.palette.error.main : theme.palette.primary.main}`,
							borderRadius: 2,
						}}
					>
						<CardElement
							onChange={handleCardChange}
							options={{
								style: {
									base: {
										fontSize: '20px',
										backgroundColor: '#f8f4fc',
										color: formik.errors.stripe ? theme.palette.error.main : theme.palette.primary.main,
										'::placeholder': {
											color: '#aaaaaa',
										},
									},
									invalid: {
										color: theme.palette.error.main,
									},
								},
								hidePostalCode: true,
							}}
						/>
					</Box>
				</FormField>
			</Collapse>
			<Box sx={{ paddingRight: { xs: 0, md: '25%' } }}>
				<Grid container spacing={{ xs: 2 }}>
					<Grid
						size={{ xs: 12, md: 4 }}
						sx={{
							textAlign: 'right',
							p: 0,
							display: { xs: 'none', md: 'flex' },
						}}
					>
						&nbsp;
					</Grid>
					<Grid size={{ xs: 12, md: 8 }}>
						<LoadingButton
							variant='contained'
							color='secondary'
							fullWidth
							size='large'
							startIcon={<PaymentOutlined />}
							type='submit'
							loading={submitting}
							disabled={isButtonDisabled()}
						>
							Make Payment
						</LoadingButton>

						<Box
							sx={{
								paddingTop: 4,
								maxWidth: 500,
								textAlign: 'center',
								margin: 'auto',
							}}
						>
							<DirectPaymentVendors />
						</Box>
					</Grid>
				</Grid>
			</Box>
		</FieldSet>
	);
};

export default StripePaymentForm;
